<template>
  <div class="card d-flex">
    <div :class="['card__container']">
      <div
        v-if="selected"
        class="card__selected"
      />
      <div
        class="card__overlay"
        @click="select"
      >
        <div
          v-if="globalCompanyPermission && globalUserPermission"
          class="card__actions"
        >
          <button
            :id="`farPen${info.id}`"
            :class="['btn', 'btn-default']"
            @click="(e) => {
              e.stopPropagation()
              edit(data.id, data.editorTypeId)
            }"
          >
            <font-awesome-icon
              :icon="['far', 'pen']"
              size="lg"
            />
          </button>
          <button
            :id="`farEye${info.id}`"
            :class="['btn', 'btn-default']"
            @click="(e) => {
              e.stopPropagation()
              onPreview(data.id)
            }"
          >
            <font-awesome-icon
              :icon="['fal', 'eye']"
              size="lg"
            />
          </button>
          <button
            :id="`farCopy${info.id}`"
            :class="['btn', 'btn-default']"
            @click="(e) => {
              e.stopPropagation()
              clone(data.id, data.editorTypeId)
            }"
          >
            <font-awesome-icon
              :icon="['fal', 'copy']"
              size="lg"
            />
          </button>
          <button
            :id="`remove${info.id}`"
            :class="['btn', 'btn-default', 'removeButton']"
            @click="(e) => {
              e.stopPropagation()
              remove(data.id)
            }"
          >
            <font-awesome-icon
              :icon="['fal', 'trash']"
              size="lg"
            />
          </button>
        </div>
        <div
          v-else
          class="card__actions"
        >
          <button
            :id="`farEye${info.id}`"
            :class="['btn', 'btn-default']"
            @click="(e) => {
              e.stopPropagation()
              onPreview(data.id)
            }"
          >
            <font-awesome-icon
              :icon="['fal', 'eye']"
              size="lg"
            />
          </button>
        </div>
        <button
          v-if="globalCompanyPermission && globalUserPermission"
          :id="`btnViewDetail${data.id}`"
          class="btn btn-lg btn-primary px-4 white card__view_details"
          @click="(e) => {
            e.stopPropagation()
            details(data.id)
          }"
        >
          View Details
        </button>
        <button
          v-else
          :id="`btnViewDetail${data.id}`"
          class="btn btn-lg btn-primary px-4 white card__view_details"
          @click="(e) => {
            e.stopPropagation()
            prebuiltClone(data.id, data.editorTypeId)
          }"
        >
          Use this Template
        </button>
      </div>
      <div class="card__content">
        <div class="card__header">
          <img
            v-show="data.base64Thumbnail"
            :src="'data:image/png;base64,' + data.base64Thumbnail"
            alt="thumbnail"
          >
        </div>
        <div class="card__info">
          <h4 class="mb-0 pb-3 template-name">
            {{ data.name }}
          </h4>
          <p
            class="card__tags"
          >
            <font-awesome-icon
              v-show="data.tags"
              :icon="['fal', 'tag']"
              class="tag__icon"
            />&nbsp;
            <span>{{ data.tags }}</span>
          </p>
        </div>
        <div class="card__footer d-flex">
          <div
            v-if="globalCompanyPermission && globalUserPermission"
            class="card__campaign_stats"
          >
            <span v-if="data.active">
              <svg class="active-icon-svg" width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="4" cy="4.5" r="4" fill="#008A50"/>
              </svg>
              Active
            </span>
            <span v-else>
              <svg class="inactive-icon-svg" width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="4" cy="4.5" r="4" fill="#A82823"/>
              </svg>
              Inactive
            </span>
            <span class="ml-4">
              <font-awesome-icon :icon="['fal', 'save']" class="icon-color"/>
              <span class="card__campaign_stats">
                {{ templateDate | dateOnlyFormat }}
              </span>
            </span>
          </div>
          <div class="card__status">
            <span
              v-if="data.editorTypeId === 1"
              class="studio-editor-template"
            >Studio Editor</span>
            <span
              v-if="data.editorTypeId === 2"
              class="drag-drop-editor-template"
            >Classic Drag-and-Drop</span>
            <span
              v-if="data.editorTypeId === 3"
              class="custom-code-editor-template"
            >Custom Code</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'TemplateCard',
  props: {
    info: [Object, Proxy],
    onPreview: Function,
    details: Function,
    edit: Function,
    clone: Function,
    prebuiltClone: Function,
    remove: Function
  },
  data () {
    return {
      data: this.info
    }
  },
  computed: {
    ...mapState({
      editorsTypes: state => state.templateBuilder.editorsTypes
    }),
    ...mapGetters({
      selectedTemplate: 'getSelectedTemplate',
      globalCompanyPermission: 'getGlobalCompanyPermission',
      globalUserPermission: 'getGlobalUserPermission'
    }),
    selected () {
      return this.selectedTemplate && this.selectedTemplate.id === this.info.id
    },
    templateDate () {
      return this.data.updateDate ?? this.data.createDate
    }
  },
  methods: {
    getEditorTypeDescription (editorTypeId) {
      if (this.editorsTypes && editorTypeId) {
        return this.editorsTypes.find(e => e.id === editorTypeId)?.description
      }
      return null
    },
    select () {
      this.$store.commit('SET_SELECTED_TEMPLATE', this.info)
    }
  }
}
</script>

<style scoped lang="scss">
  .card {
    margin: 0;
    padding: 15px;
    box-shadow: none;
    border: 0;
    border-color: none;
    flex-basis: 100%;
    cursor: pointer;

    &__container {
      position: relative;
      border: 1px solid rgba(0, 0, 0, 0.1);
      flex-basis: 100%;
      box-shadow: 0 0.125rem 0.5rem -0.0625rem rgba(0, 0, 0, 0.05);
      margin-bottom: 1rem;
    }

    &__info {
      h4 {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }

      p {
        font-size: 1.4rem;
      }

      .card__tags {
        min-height: 21px;

        .tag__icon {
          font-size: 1.6rem;
          vertical-align: middle;
        }
      }
    }

    &__overlay, &__selected {
      position: absolute;
      width: 100%;
      height: 100%;
      background-color: transparent;
      opacity: 0;
      transition: 250ms all ease-in;
      z-index: 3;
      border: 2px solid #177eab;
      justify-content: center;
      align-items: center;
      overflow: hidden;
    }

    &__selected {
      opacity: 1;
    }

    &:hover {
      .card__overlay {
        background-color: #ffffffcc;
        opacity: 1;
      }
    }

    &__actions, &__view_details {
      position: absolute;
    }

    &__actions {
      top: -5px;
      right: 0;
      background-color: #177eab;

      .btn {
        border-radius: 0;
        padding: 6px;
        padding-top: 9px;
        color: white;
      }

      .btn + .btn {
        margin-left: 0;
      }
    }

    &__view_details {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &__content > div:not(:first-child) {
      padding: 15px;
    }

    &__header {
      height: 180px;
      overflow: hidden;
      text-align:center;
      img {
        height: 180px;
        width: 100%;
        max-width: 500px;
        margin: auto;
      }
    }

    &__tags {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__footer {
      border-top: 1px solid rgba(0, 0, 0, 0.1);
      justify-content: space-between;

      .card__status {
        margin-left: auto;
        svg {
          width: 8px;
          margin-right: 7px;
        }
      }
    }

    &__campaign_stats {
      flex-basis: 60%;

      &--blue {
        color: $matisse;
      }

      svg {
        margin-right: 8px;
      }
    }

    @media screen and (min-width: 520px) {
      flex-basis: calc(50%);
    }

    @media screen and (min-width: 768px) {
      flex-basis: calc(100%/3);
    }
  }
.success {
  color: $success;
}
.danger {
  color: $danger;
}
.unset {
  color: $gray-medium;
}

.icon-color{
  color: #000000;
}

.studio-editor-template{
  border-radius: 32px;
  text-align: center;
  margin-right: 4px;
  position: relative;
  display: inline-block;
  background-color: #E0DFE9;
  color: #24245A;
  margin-right: 4px;
  padding: 0 1rem 0 1rem;
  font-size: 14px;
  font-weight: 600;
}

.drag-drop-editor-template{
  border-radius: 32px;
  text-align: center;
  margin-right: 4px;
  position: relative;
  display: inline-block;
  background-color: #FAEFDA;
  color: #BA841B;
  margin-right: 4px;
  padding: 0 1rem 0 1rem;
  font-size: 14px;
  font-weight: 600;
}

.custom-code-editor-template{
  border-radius: 32px;
  text-align: center;
  margin-right: 4px;
  position: relative;
  display: inline-block;
  background-color: #E9F2FA;
  color: #4484BD;
  margin-right: 4px;
  padding: 0 1rem 0 1rem;
  font-size: 14px;
  font-weight: 600;
}

.active-icon-svg,
.inactive-icon-svg{
  margin-bottom: 2px;
}

.template-name{
  font-weight: 600
}
</style>
